import { useEffect, useState, FormEvent } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const location = useLocation();
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const companyAlt = formData.get("company_alt") as string;
    if (companyAlt && companyAlt.trim() !== "") {
      setLoading(false);
      setSubmitted(true);
      return;
    }

    try {
      const res = await fetch(
        "https://readdy.ai/api/form/d9u58o53pcjqs2fcsmc0",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            name: formData.get("name") as string,
            email: formData.get("email") as string,
            message: formData.get("message") as string,
          }).toString(),
        }
      );
      const responseText = await res.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      if (res.ok && parsed?.code === "OK") {
        setSubmitted(true);
        setFormState({ name: "", email: "", message: "" });
      } else {
        const msg =
          parsed?.meta?.message ||
          parsed?.message ||
          parsed?.meta?.detail ||
          "提交失败，请稍后再试";
        setError(msg);
      }
    } catch {
      setError("网络错误，请检查您的网络连接后重试");
    }
    setLoading(false);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于我们 - 佐佐木希官方粉丝资讯站",
            description:
              "佐佐木希官方粉丝资讯站品牌介绍、口碑评价及联系方式",
            url: "https://cfcfo.cn/about",
            isPartOf: {
              "@type": "WebSite",
              name: "佐佐木希官方粉丝资讯站",
              url: "https://cfcfo.cn/",
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "本站是佐佐木希的官方网站吗？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "本站是粉丝自发创建和维护的非官方资讯平台，非佐佐木希或所属事务所的官方网站。所有内容仅供粉丝交流使用。",
                },
              },
              {
                "@type": "Question",
                name: "如何获取佐佐木希最新写真？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "请关注本站「写真精选」栏目以及订阅我们的邮件通知，我们会在第一时间发布佐佐木希最新写真集和杂志拍摄的相关资讯。",
                },
              },
              {
                "@type": "Question",
                name: "佐佐木希的最新影视作品在哪可以观看？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "本站「影视作品」栏目会及时更新佐佐木希的最新出演信息。具体观看渠道因地区而异，请留意各视频平台的播出信息。",
                },
              },
              {
                "@type": "Question",
                name: "如何参与粉丝应援活动？",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "请关注「粉丝应援」栏目及我们的社交媒体账号，我们会定期发布应援活动的相关信息和参与方式。",
                },
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-20 bg-background-50">
        <div className="max-w-screen-lg mx-auto px-4 md:px-6">
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>

          {/* Brand Introduction */}
          <section className="py-12">
            <h1
              className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              关于我们
            </h1>
            <p className="text-foreground-600 text-base leading-relaxed mb-8 max-w-2xl">
              佐佐木希官方粉丝资讯站是一个由粉丝自发创建和维护的非官方中文资讯平台，致力于为全球华人粉丝提供关于佐佐木希最全面、最及时的资讯内容。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className="ri-heart-line text-lg"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                  我们的使命
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  汇集佐佐木希最新写真、影视作品、时尚造型及粉丝应援资讯，为中文粉丝打造一个温馨、专业、及时的交流平台，让更多人了解和喜爱这位来自秋田县的国民女神。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className="ri-star-line text-lg"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                  我们的愿景
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  成为中文世界最具影响力的佐佐木希粉丝资讯平台，持续为粉丝提供高质量的内容和服务，搭建粉丝与艺人之间的文化桥梁。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className="ri-team-line text-lg"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                  我们的团队
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  由一群热爱日本影视文化的资深粉丝组成，我们中有日语翻译、时尚编辑、摄影爱好者和内容创作者，共同为粉丝提供专业的内容服务。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                  <i className="ri-shield-check-line text-lg"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-2">
                  内容准则
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有内容均来源于公开渠道，尊重版权和艺人隐私。我们坚持客观、真实、积极的内容导向，营造健康的粉丝文化交流环境。
                </p>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section id="reputation" className="py-12 border-t border-background-200/70">
            <h2
              className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              口碑评价
            </h2>
            <p className="text-foreground-600 text-sm mb-8">
              来自粉丝的真实评价与反馈
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  这个粉丝站做得太用心了！内容更新及时，写真集和影视作品的整理非常全面，中文化的内容让我这样的日语不好的粉丝也能了解佐佐木希的最新动态。已收藏！
                </p>
                <p className="text-xs text-foreground-500 font-medium">
                  — 东京塔上的猫（来自微博）
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  时尚造型板块是我最喜欢的部分，佐佐木希的穿搭真的太有气质了！GU联名那篇文章写得特别好，我已经按文中的推荐入手了好几件单品。
                </p>
                <p className="text-xs text-foreground-500 font-medium">
                  — 樱花少女酱（来自小红书）
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3 text-yellow-500">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  作为老粉丝，看到有这样一个用心的中文资讯站真的很感动。文章质量高，排版也好看，看得出运营者的用心。希望站点越办越好！
                </p>
                <p className="text-xs text-foreground-500 font-medium">
                  — 秋田的风（来自知乎）
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-12 border-t border-background-200/70">
            <h2
              className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              常见问题
            </h2>
            <div className="space-y-4 max-w-3xl">
              {[
                {
                  q: "本站是佐佐木希的官方网站吗？",
                  a: "本站是粉丝自发创建和维护的非官方资讯平台，非佐佐木希或所属事务所的官方网站。所有内容仅供粉丝交流使用。",
                },
                {
                  q: "如何获取佐佐木希最新写真？",
                  a: "请关注本站「写真精选」栏目以及订阅我们的邮件通知，我们会在第一时间发布佐佐木希最新写真集和杂志拍摄的相关资讯。",
                },
                {
                  q: "佐佐木希的最新影视作品在哪可以观看？",
                  a: "本站「影视作品」栏目会及时更新佐佐木希的最新出演信息。具体观看渠道因地区而异，请留意各视频平台的播出信息。",
                },
                {
                  q: "如何参与粉丝应援活动？",
                  a: "请关注「粉丝应援」栏目及我们的社交媒体账号，我们会定期发布应援活动的相关信息和参与方式。",
                },
              ].map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-100 rounded-lg border border-background-200/70"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer">
                    <span className="text-sm font-medium text-foreground-950">
                      {faq.q}
                    </span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="py-12 border-t border-background-200/70">
            <h2
              className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              联系我们
            </h2>
            <p className="text-foreground-600 text-sm mb-8">
              如有任何问题或合作意向，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                    <p className="text-sm text-foreground-600">info@cfcfo.cn</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">所在地</p>
                    <p className="text-sm text-foreground-600">日本东京都</p>
                  </div>
                </div>
              </div>

              <div>
                {submitted ? (
                  <div className="bg-accent-100/50 rounded-lg p-6 border border-accent-200/50">
                    <p className="text-accent-700 font-medium">
                      消息已成功发送！感谢您的联系，我们会尽快回复。
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form="true"
                    id="contact-form"
                    className="space-y-4"
                  >
                    <div>
                      <label
                        htmlFor="contact-name"
                        className="block text-sm font-medium text-foreground-700 mb-1.5"
                      >
                        姓名
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        value={formState.name}
                        onChange={(e) =>
                          setFormState({ ...formState, name: e.target.value })
                        }
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200/70 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-email"
                        className="block text-sm font-medium text-foreground-700 mb-1.5"
                      >
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        value={formState.email}
                        onChange={(e) =>
                          setFormState({ ...formState, email: e.target.value })
                        }
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200/70 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-sm font-medium text-foreground-700 mb-1.5"
                      >
                        留言
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formState.message}
                        onChange={(e) =>
                          setFormState({ ...formState, message: e.target.value })
                        }
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-100 border border-background-200/70 text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                      <p className="text-xs text-foreground-500 mt-1">
                        {formState.message.length}/500
                      </p>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="phone-alt-field"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
                    >
                      {loading ? "发送中..." : "发送消息"}
                    </button>
                    {error && (
                      <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2">
                        {error}
                      </p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}